import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Zap,
  Lock,
  Phone,
  MessageCircle,
  Building2,
  Users,
  Briefcase,
  Clock,
  IndianRupee,
} from "lucide-react";
import { useLiveMarket } from "@/lib/useLiveMarket";
import OtcInquiryModal from "@/components/OtcInquiryModal";
import Reveal from "../Reveal";

const COIN_IDS = ["bitcoin", "tether"];
const COIN_META = {
  bitcoin: { sym: "BTC", name: "Bitcoin", letter: "₿", color: "#F7931A" },
  tether: { sym: "USDT", name: "Tether", letter: "₮", color: "#26A17B" },
};

const USD_INR = 83.5;
const MIN_TICKET_LABEL = "₹10 L";

const STEPS = [
  { n: "01", title: "Submit request", desc: "Choose USDT or BTC and enter your INR amount" },
  { n: "02", title: "RM confirms", desc: "Your Relationship Manager shares a live quote and confirms the price with you" },
  { n: "03", title: "Wallet credit", desc: "Once confirmed, assets are credited directly to your wallet" },
];

const AUDIENCE = [
  { icon: Building2, label: "Treasury desks" },
  { icon: Users, label: "Family offices" },
  { icon: Briefcase, label: "HNIs & funds" },
];

function fmtINR(v) {
  if (!Number.isFinite(v)) return "—";
  if (v >= 10_000_000)
    return `₹${(v / 10_000_000).toLocaleString("en-IN", { maximumFractionDigits: 2 })} Cr`;
  if (v >= 100_000)
    return `₹${(v / 100_000).toLocaleString("en-IN", { maximumFractionDigits: 2 })} L`;
  return `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function fmtUsd(n) {
  if (!Number.isFinite(n)) return "—";
  return `$${n.toLocaleString("en-US", { maximumFractionDigits: n >= 100 ? 2 : 4 })}`;
}

export default function OtcDesk() {
  const [modalOpen, setModalOpen] = useState(false);
  const live = useLiveMarket(COIN_IDS);

  return (
    <section
      className="relative overflow-hidden py-20 lg:py-28 bg-cm-surface border-y border-[var(--cm-border)]"
      data-testid="otc-section"
    >
      <div className="absolute inset-0 cm-landing-grid-bg opacity-40 pointer-events-none" />
      <div
        className="absolute top-0 right-0 w-[min(520px,50vw)] h-[min(520px,50vh)] rounded-full opacity-30 blur-3xl pointer-events-none"
        style={{
          background: "radial-gradient(circle, rgba(2,44,237,0.2) 0%, transparent 70%)",
        }}
      />

      <div className="cm-container relative">
        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-14 items-start">
          <Reveal variant="fade-right" className="lg:sticky lg:top-24">
            <span className="cm-landing-eyebrow">
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
              </span>
              OTC desk · now open
            </span>

            <h2 className="mt-5 text-[32px] lg:text-[44px] font-extrabold text-cm-navy tracking-tight leading-[1.08]">
              Trade large volumes,{" "}
              <span className="cm-landing-hero-accent">privately.</span>
            </h2>

            <p className="mt-5 text-[15px] lg:text-[16px] leading-[1.75] text-cm-grey max-w-[540px]">
              Buy USDT and BTC in size — off-exchange execution for treasuries, family
              offices, and HNIs. Move {MIN_TICKET_LABEL} to {fmtINR(50_000_000)}+ in a single
              ticket without slippage on the public order book.
            </p>

            <div className="mt-6 flex flex-wrap gap-2">
              {AUDIENCE.map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white border border-[var(--cm-border)] text-[12px] font-semibold text-cm-navy"
                >
                  <Icon size={14} className="text-cm-blue" />
                  {label}
                </span>
              ))}
            </div>

            <div className="mt-8 grid sm:grid-cols-3 gap-3">
              <BenefitCard icon={Zap} title="Tighter spreads" sub="vs. open market" />
              <BenefitCard icon={Lock} title="Private execution" sub="Off order book" />
              <BenefitCard icon={ShieldCheck} title="Same-day settle" sub="INR or crypto" />
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <button
                onClick={() => setModalOpen(true)}
                data-testid="otc-cta-primary"
                className="group inline-flex items-center justify-center gap-2 h-[52px] px-8 rounded-full bg-cm-blue text-white font-bold text-[15px] shadow-[0_16px_40px_-14px_rgba(2,44,237,0.5)] hover:bg-cm-blue-deep hover:-translate-y-0.5 transition-all"
              >
                Submit OTC
                <ArrowRight size={18} className="group-hover:translate-x-0.5 transition-transform" />
              </button>
              <a
                href="https://wa.me/918796101200"
                target="_blank"
                rel="noreferrer"
                data-testid="otc-cta-whatsapp"
                className="inline-flex items-center justify-center gap-2 h-[52px] px-7 rounded-full bg-white border-2 border-cm-blue/20 text-cm-blue font-semibold text-[14px] hover:bg-cm-lavender/60 transition"
              >
                <MessageCircle size={17} /> WhatsApp desk
              </a>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-cm-grey">
              <span className="inline-flex items-center gap-1.5">
                <Phone size={14} className="text-cm-blue" />
                <a href="tel:+918796101200" className="font-semibold text-cm-navy hover:text-cm-blue">
                  +91 87961 01200
                </a>
              </span>
              <span className="text-cm-border">|</span>
              <span className="inline-flex items-center gap-1.5">
                <Clock size={14} className="text-cm-blue" />
                24×7 desk
              </span>
            </div>
          </Reveal>

          <Reveal variant="fade-left" delay={120} className="space-y-5">
            <div
              className="rounded-[20px] border border-[var(--cm-border)] bg-white p-6 lg:p-7 shadow-[0_24px_60px_-32px_rgba(29,45,63,0.18)]"
              data-testid="otc-quote-panel"
            >
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <div className="cm-landing-section-label mb-2">Indicative quote</div>
                  <h3 className="text-[20px] font-bold text-cm-navy">Live OTC pricing</h3>
                  <p className="text-[12px] text-cm-grey mt-1">Spot reference · INR approx. @ ₹{USD_INR}</p>
                </div>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-bold">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Live
                </span>
              </div>

              <div className="space-y-3">
                {COIN_IDS.map((id) => {
                  const m = COIN_META[id];
                  const t = live[id];
                  const price = t?.price;
                  const change = t?.changePct;
                  const up = (change ?? 0) >= 0;
                  const inr = Number.isFinite(price) ? price * USD_INR : null;

                  return (
                    <div
                      key={id}
                      className="flex items-center justify-between gap-4 p-4 rounded-[14px] bg-cm-surface border border-[var(--cm-border)] hover:border-cm-blue/25 transition-colors"
                      data-testid={`otc-live-${m.sym.toLowerCase()}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span
                          className="w-11 h-11 rounded-full grid place-items-center text-white text-[15px] font-bold shrink-0"
                          style={{ background: m.color }}
                        >
                          {m.letter}
                        </span>
                        <div className="min-w-0">
                          <div className="text-[15px] font-bold text-cm-navy">{m.name}</div>
                          <div className="text-[11px] text-cm-grey">{m.sym} · USD spot</div>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[16px] font-extrabold text-cm-navy tabular-nums">
                          {fmtUsd(price)}
                        </div>
                        <div className="text-[12px] text-cm-grey tabular-nums flex items-center justify-end gap-1 mt-0.5">
                          <IndianRupee size={11} />
                          {Number.isFinite(inr)
                            ? inr.toLocaleString("en-IN", { maximumFractionDigits: 0 })
                            : "—"}
                        </div>
                        <div
                          className={`text-[11px] font-bold tabular-nums mt-1 ${
                            up ? "text-emerald-600" : "text-red-500"
                          }`}
                        >
                          {Number.isFinite(change)
                            ? `${up ? "+" : ""}${change.toFixed(2)}% 24h`
                            : "—"}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 grid grid-cols-3 gap-3">
                {[
                  { label: "Min ticket", value: MIN_TICKET_LABEL },
                  { label: "Settlement", value: "T+0" },
                  { label: "Fee model", value: "All-in quote" },
                ].map((s) => (
                  <div
                    key={s.label}
                    className="rounded-[12px] bg-cm-lavender/50 border border-cm-blue/10 px-3 py-3 text-center"
                  >
                    <div className="text-[10px] font-bold uppercase tracking-wider text-cm-grey">
                      {s.label}
                    </div>
                    <div className="text-[13px] font-bold text-cm-navy mt-1">{s.value}</div>
                  </div>
                ))}
              </div>

              <Link
                to="/otc"
                data-testid="otc-cta-rfq"
                className="mt-5 w-full inline-flex items-center justify-center gap-2 h-[48px] rounded-full bg-cm-blue text-white text-[14px] font-bold hover:bg-cm-blue-deep transition"
              >
                Get a live quote <ArrowRight size={16} />
              </Link>
              <p className="mt-3 text-[11px] text-cm-grey text-center leading-relaxed">
                Indicative only · RM-assisted confirmation · KYC required
              </p>
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              {STEPS.map((step) => (
                <div
                  key={step.n}
                  className="cm-landing-feature-card p-4 bg-white"
                  data-testid={`otc-step-${step.n}`}
                >
                  <div className="text-[11px] font-extrabold text-cm-blue tracking-wider">{step.n}</div>
                  <div className="text-[14px] font-bold text-cm-navy mt-2">{step.title}</div>
                  <p className="text-[12px] text-cm-grey mt-1 leading-snug">{step.desc}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>

      <OtcInquiryModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </section>
  );
}

function BenefitCard({ icon: Icon, title, sub }) {
  return (
    <div className="flex items-center gap-3 p-3.5 rounded-[14px] bg-white border border-[var(--cm-border)]">
      <div className="w-10 h-10 rounded-xl bg-cm-lavender grid place-items-center shrink-0">
        <Icon size={18} className="text-cm-blue" strokeWidth={1.75} />
      </div>
      <div>
        <div className="text-[13px] font-bold text-cm-navy leading-tight">{title}</div>
        <div className="text-[11px] text-cm-grey mt-0.5">{sub}</div>
      </div>
    </div>
  );
}
